import React, { useMemo } from 'react';

// Table Sort Configuration Component
// Allows sorting table rows by any configured column
const TableSortConfiguration = ({ block, onUpdateSort, extractDataObj, dataContext, columnLabels }) => {
    const tableSort = block.dataConfig?.tableSort || null;
    const tableColumns = block.dataConfig?.tableColumns || [];

    // Identify available columns for this widget
    const availableFields = useMemo(() => {
        // 1. Hardcoded fallbacks for Finding List widgets that don't have extractDataObj
        if (block.widgetType === 'key-findings') {
            return [
                { label: 'Category', value: 'category' },
                { label: 'Status', value: 'status' },
                { label: 'Compliance', value: 'compliance' }
            ];
        }
        if (block.widgetType === 'open-ports') {
            return [
                { label: 'IP Address', value: 'ip' },
                { label: 'Port', value: 'port' },
                { label: 'Service', value: 'service' }
            ];
        }
        if (block.widgetType === 'findings-grid') {
            return [
                { label: 'Title', value: 'title' },
                { label: 'Score', value: 'score' },
                { label: 'Color/Status', value: 'color' }
            ];
        }

        // 2. Logic for Standard and Proof Tables
        let fields = [];
        if (extractDataObj && typeof extractDataObj.extractor === 'function') {
            // Proof Table logic
            try {
                const rawRows = extractDataObj.extractor(dataContext, block.dataConfig) || [];
                let columns = [];
                if (extractDataObj.availableColumns) {
                    columns = extractDataObj.availableColumns;
                } else if (rawRows.length > 0) {
                    columns = Object.keys(rawRows[0]);
                }
                
                fields = columns.map((col, index) => {
                    // Try to use the provided columnLabels mapping if it exists
                    const label = columnLabels && columnLabels[index] ? columnLabels[index] : col;
                    return {
                        label: label,
                        value: col
                    };
                });
            } catch (err) {
                console.error('[TableSortConfiguration] Error extracting columns', err);
                fields = [];
            }
        } else {
            // Standard Table logic
            fields = tableColumns
                .filter(col => col.fieldPath && col.fieldPath.trim() !== '')
                .map(col => ({
                    label: col.header || col.fieldPath,
                    value: col.fieldPath
                }));
        }

        return fields;
    }, [extractDataObj, dataContext, block.dataConfig, tableColumns, block.widgetType, columnLabels]);

    const updateSort = (fieldPath, direction) => {
        if (!fieldPath || fieldPath === '') {
            onUpdateSort(null);
        } else {
            onUpdateSort({ fieldPath, direction });
        }
    };

    const clearSort = () => {
        onUpdateSort(null);
    };

    return (
        <div className="table-sort-config" style={{ marginTop: '1rem' }}>
            <div className="property-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    🔀 Table Sorting
                </label>
                <small className="help-text" style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                    Sort table rows by any configured column. Only columns you've added to the table are available for sorting.
                </small>

                {availableFields.length === 0 && (
                    <div style={{
                        padding: '0.75rem',
                        textAlign: 'center',
                        color: 'var(--text-secondary)',
                        border: '1px dashed var(--border)',
                        borderRadius: 'var(--radius)',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem'
                    }}>
                        No columns available for sorting.
                    </div>
                )}

                {availableFields.length > 0 && (
                    <div style={{
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        backgroundColor: 'var(--background)'
                    }}>
                        {/* Sort Column Selection */}
                        <div style={{ marginBottom: '0.75rem' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                            }}>
                                Sort By Column:
                            </label>
                            <select
                                value={tableSort?.fieldPath || ''}
                                onChange={(e) => {
                                    const selectedPath = e.target.value;
                                    if (selectedPath === '') {
                                        clearSort();
                                    } else {
                                        // Preserve existing direction or default to 'asc'
                                        updateSort(selectedPath, tableSort?.direction || 'asc');
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: 'var(--radius)',
                                    border: '1px solid var(--border)',
                                    fontSize: '0.875rem'
                                }}
                            >
                                <option value="">-- No Sorting --</option>
                                {availableFields.map(field => (
                                    <option key={field.value} value={field.value}>
                                        {field.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Sort Direction */}
                        {tableSort && tableSort.fieldPath && (
                            <div style={{ marginBottom: '0.75rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500'
                                }}>
                                    Sort Direction:
                                </label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => updateSort(tableSort.fieldPath, 'asc')}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem',
                                            borderRadius: 'var(--radius)',
                                            border: '1px solid var(--border)',
                                            backgroundColor: tableSort.direction === 'asc' ? 'var(--primary)' : 'var(--background)',
                                            color: tableSort.direction === 'asc' ? 'white' : 'var(--text)',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            fontWeight: tableSort.direction === 'asc' ? '600' : '400',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.25rem'
                                        }}
                                    >
                                        ↑
                                        Ascending
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateSort(tableSort.fieldPath, 'desc')}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem',
                                            borderRadius: 'var(--radius)',
                                            border: '1px solid var(--border)',
                                            backgroundColor: tableSort.direction === 'desc' ? 'var(--primary)' : 'var(--background)',
                                            color: tableSort.direction === 'desc' ? 'white' : 'var(--text)',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            fontWeight: tableSort.direction === 'desc' ? '600' : '400',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.25rem'
                                        }}
                                    >
                                        ↓
                                        Descending
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TableSortConfiguration;
